import React, { useState, useEffect, useRef } from 'react';
import { Box, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useDestrierTheme } from '../../ThemeContext';
import { API_BASE } from '../../api';

// Hook to fetch waitlist count once and cache it
export const _waitlistCache = { count: null, promise: null };
export function useWaitlistCount() {
  const [count, setCount] = useState(_waitlistCache.count);
  useEffect(() => {
    if (_waitlistCache.count !== null) { setCount(_waitlistCache.count); return; }
    if (!_waitlistCache.promise) {
      _waitlistCache.promise = fetch(`${API_BASE}/waitlist/count`)
        .then(r => r.json())
        .then(d => { _waitlistCache.count = d.count; return d.count; })
        .catch(() => null);
    }
    _waitlistCache.promise.then(c => { if (c !== null) setCount(c); });
  }, []);
  return count;
}

// Hook to add non-passive touch listeners for proper preventDefault
export function useSwipeBlock(ref, touchRef) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (e) => {
      if (touchRef.current && touchRef.current.locked === 'h') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    el.addEventListener('touchmove', handler, { passive: false });
    return () => el.removeEventListener('touchmove', handler);
  }, [ref, touchRef]);
}

// Shared slide variants for directional carousels
export const slideVariants = {
  enter: (dir) => ({ x: dir * 300 }),
  center: { x: 0 },
  exit: (dir) => ({ x: dir * -300 }),
};

// ---- Palettes ----
export const destrierPalette = {
  bg: '#222226',
  bgCard: '#252528',
  bgCardHover: '#2d2d32',
  accent: '#8A74E4',
  accentGlow: '#8A74E440',
  blue: '#00A5C4',
  ctaBlue: '#3DB5D4',
  blueGlow: '#00A5C430',
  gold: '#DE7345',
  textPrimary: '#ECE2DB',
  textSecondary: '#8a8a9a',
  textMuted: '#5a5a6a',
  border: '#ffffff0a',
  borderHover: '#ffffff15',
};

export const legacyPalette = {
  bg: '#1a1a1e',
  bgCard: '#222226',
  bgCardHover: '#2a2a2e',
  accent: '#f5911f',
  accentGlow: '#f5911f40',
  blue: '#15d7ff',
  ctaBlue: '#3399C4',
  blueGlow: '#15d7ff30',
  gold: '#fbbf24',
  textPrimary: '#ECE2DB',
  textSecondary: '#8a8a9a',
  textMuted: '#5a5a6a',
  border: '#ffffff0a',
  borderHover: '#ffffff15',
};

// Shared fluid type scale — clamp(min, preferred, max). `link` is the baseline
// body size used in the footer menu items; treat it as the minimum body size.
export const fontSize = {
  heading: 'clamp(1rem, 0.85rem + 0.55vw, 1.6rem)',
  link:    'clamp(1rem, 0.9rem + 0.45vw, 1.45rem)',
  body:    'clamp(0.9rem, 0.8rem + 0.35vw, 1.2rem)',
  small:   'clamp(0.85rem, 0.75rem + 0.25vw, 1.05rem)',
};

// Global color overrides for debug panel
export let _colorOverrides = {};
export let _colorListeners = new Set();
export function setColorOverride(key, value) {
  _colorOverrides = { ..._colorOverrides, [key]: value };
  _colorListeners.forEach(fn => fn());
}
export function resetColorOverrides() { // eslint-disable-line no-unused-vars
  _colorOverrides = {};
  _colorListeners.forEach(fn => fn());
}

export const useColors = () => {
  const { c, isDark, scheme } = useDestrierTheme(); // eslint-disable-line no-unused-vars
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1);
    _colorListeners.add(listener);
    return () => _colorListeners.delete(listener);
  }, []);

  const palette = scheme === 'legacy' ? legacyPalette : destrierPalette;

  let base;
  if (c.mode === 'dark') {
    base = { ...palette, isDark: true, cream: c.cream, textDark: c.textDark };
  } else {
    base = {
      ...palette,
      isDark: false,
      cream: c.cream,
      textDark: c.textDark,
      bg: scheme === 'legacy' ? '#fef5e7' : '#ECE2DB',
      bgCard: scheme === 'legacy' ? '#fefcf8' : '#E4D8CF',
      bgCardHover: scheme === 'legacy' ? '#faf6f0' : '#ddd0c5',
      textPrimary: scheme === 'legacy' ? '#1e293b' : '#222226',
      textSecondary: '#64748b',
      textMuted: '#94a3b8',
      border: '#00000008',
      borderHover: '#00000012',
    };
  }
  return { ...base, ..._colorOverrides, scheme };
};

// ---- SVG Icons ----
export const SvgIcon = ({ d, color = 'currentColor', size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

export const icons = {
  trendDown: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
      <polyline points="16 17 22 17 22 11" />
    </svg>
  ),
  shuffle: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 3 21 3 21 8" />
      <line x1="4" y1="20" x2="21" y2="3" />
      <polyline points="21 16 21 21 16 21" />
      <line x1="15" y1="15" x2="21" y2="21" />
      <line x1="4" y1="4" x2="9" y2="9" />
    </svg>
  ),
  helpCircle: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  monitor: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  crown: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20h20L19 8l-5 5-2-7-2 7-5-5z" />
    </svg>
  ),
  target: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  clock: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  book: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  ),
  zap: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  swords: (color, size) => (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="2" y1="2" x2="16" y2="16" />
      <line x1="2" y1="6" x2="2" y2="2" />
      <line x1="6" y1="2" x2="2" y2="2" />
      <line x1="22" y1="2" x2="8" y2="16" />
      <line x1="22" y1="6" x2="22" y2="2" />
      <line x1="18" y1="2" x2="22" y2="2" />
      <line x1="9" y1="15" x2="4" y2="20" />
      <line x1="15" y1="9" x2="20" y2="4" />
    </svg>
  ),
};

// ---- Visible-on-scroll wrapper ----
// Renders `null` (a height-reserving placeholder) until the wrapper is within
// `rootMargin` of the viewport, then renders children. Used to defer BOTH the
// JS chunk download and component mount of below-fold lazy sections until the
// user scrolls close — keeps the hero's initial paint fast.
//
// Each wrapper includes its own <Suspense fallback={null}> so that one section's
// code-split chunk loading doesn't suspend the rest of the tree.
export function VisibleOnScroll({ children, minHeight = 400, rootMargin = '400px' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible, rootMargin]);
  return (
    <div ref={ref} style={!visible ? { minHeight } : undefined}>
      {visible ? <React.Suspense fallback={null}>{children}</React.Suspense> : null}
    </div>
  );
}

// ---- Fade-in on scroll ----
// Static — renders immediately with NO scroll-entrance animation. (This used to
// fade + slide up when scrolled into view; that reveal was removed site-wide per
// design.) For the few places that still want the on-scroll reveal (the FAQ
// questions), use FadeInScroll below.
export const FadeIn = ({ children, delay = 0, ...props }) => (
  <div {...props}>{children}</div>
);

// The original on-scroll reveal: starts slightly below + transparent, then slides
// up and fades in as it enters the viewport. Kept for the FAQ questions only.
export const FadeInScroll = ({ children, delay = 0, ...props }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.55, delay, ease: 'easeOut' }}
    {...props}
  >
    {children}
  </motion.div>
);

// ---- Section Chevron (clickable scroll-to-next) ----
export function SectionChevron({ nextId, bg }) {
  // eslint-disable-next-line no-unused-vars
  const { c } = useDestrierTheme();
  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 0.4 }}
      viewport={{ once: true }}
      transition={{ opacity: { duration: 0.8 } }}
      onClick={() => nextId && document.getElementById(nextId)?.scrollIntoView({ behavior: 'smooth' })}
      role="button"
      aria-label="Scroll to next section"
      sx={{ textAlign: 'center', py: 2, cursor: nextId ? 'pointer' : 'default', background: bg || 'transparent' }}
    >
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#8A74E4" strokeWidth="3.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 9l6 6 6-6" />
      </svg>
    </Box>
  );
}

// ---- Reusable cream CTA button (matches FinalCTA "Start your ascension") ----
// Cream pill on dark themes, dark pill on light themes, with optional sliding-arrow hover animation.
// `size`: 'small' (navbar), 'medium' (section CTAs), 'large' (final CTA).
// `arrow`: false to omit the arrow (use when attached to an input, in tight rows, etc.).
export function WhiteCTAButton({
  onClick,
  children,
  disabled,
  size = 'medium',
  arrow = true,
  fullWidth = false,
  sx = {},
  ...rest
}) {
  const c = useColors();
  const sizeMap = {
    small:  { py: 0.7,  px: 2.5, minHeight: 36, fontSize: 'clamp(0.85rem, 0.78rem + 0.25vw, 1rem)', arrowPx: 20 },
    medium: { py: 1.7,  px: 5,   minHeight: 52, fontSize: '1.05rem', arrowPx: 26 },
    large:  { py: { xs: 1.5, md: 2 }, px: { xs: 4, md: 5 }, minHeight: { xs: 52, md: 60 }, fontSize: fontSize.link, arrowPx: 28 },
  };
  const s = sizeMap[size] || sizeMap.medium;
  const bg = c.isDark ? '#ECE2DB' : '#22222e';
  const fg = c.isDark ? '#22222e' : '#ECE2DB';

  return (
    <Button
      variant="contained"
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      sx={{
        py: s.py,
        px: s.px,
        minHeight: s.minHeight,
        borderRadius: '100px',
        fontWeight: size === 'small' ? 600 : 500,
        fontSize: s.fontSize,
        textTransform: 'none',
        backgroundColor: bg,
        color: fg,
        boxShadow: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: arrow ? 1.25 : 0,
        '&:hover': {
          backgroundColor: bg,
          filter: 'brightness(0.92)',
          boxShadow: 'none',
        },
        '&:hover .arrow-slot svg:first-of-type': { transform: 'translateX(120%)' },
        '&:hover .arrow-slot svg:last-of-type':  { transform: 'translateX(0)' },
        '&:disabled': {
          backgroundColor: bg,
          color: fg,
          opacity: 0.6,
        },
        ...sx,
      }}
      {...rest}
    >
      {children}
      {arrow && (
        <Box
          className="arrow-slot"
          sx={{
            position: 'relative',
            width: s.arrowPx,
            height: s.arrowPx,
            overflow: 'hidden',
            display: 'inline-block',
            '& svg': {
              position: 'absolute',
              top: 0,
              left: 0,
              transition: 'transform 0.45s cubic-bezier(0.65, 0, 0.35, 1)',
            },
            '& svg:first-of-type': { transform: 'translateX(0)' },
            '& svg:last-of-type':  { transform: 'translateX(-120%)' },
          }}
        >
          <svg width={s.arrowPx} height={s.arrowPx} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
          <svg width={s.arrowPx} height={s.arrowPx} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </Box>
      )}
    </Button>
  );
}

// Generate a tiling noise texture as data URL (created once, reused everywhere)
export const GRAIN_DATA_URL = (() => {
  if (typeof document === 'undefined') return '';
  const size = 200;
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  const imageData = ctx.createImageData(size, size);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.random() * 80;
    d[i] = v; d[i + 1] = v; d[i + 2] = v; d[i + 3] = 0;
  }
  ctx.putImageData(imageData, 0, 0);
  return c.toDataURL('image/png');
})();
